import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Table } from "../components/ui/Table/Table";
import { Badge } from "../components/ui/Badge/Badge";
import { Input } from "../components/ui/Input/Input";
import { Select } from "../components/ui/Select/Select";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { EmptyState } from "../components/ui/EmptyState/EmptyState";
import { Pagination } from "../components/ui/Pagination/Pagination";
import { formatQuantity } from "../lib/date";
import {
	useWarehouseList,
	useWarehouseStocks,
	useWarehouseStockSearch,
	type WarehouseStockRow,
} from "../hooks/use-warehouses";
import { useStockItemCategories } from "../hooks/use-stock-items";
import styles from "./Warehouses.module.css";

export interface WarehousesSearch {
	warehouseId: string;
	scope: "visible" | "all";
	q: string;
	category: string;
	page: number;
	size: number;
}

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/warehouses",
	validateSearch: (search: Record<string, unknown>): WarehousesSearch => ({
		warehouseId: String(search.warehouseId || ""),
		scope: search.scope === "all" ? "all" : "visible",
		q: String(search.q || ""),
		category: String(search.category || ""),
		page: Number(search.page) || 1,
		size: Number(search.size) || 20,
	}),
	component: withPermission(WarehousesPage, "warehouses", "read"),
});

function WarehousesPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { warehouseId, scope, q, category, page, size } = route.useSearch();
	const [warehouseFilter, setWarehouseFilter] = useState("");
	const warehouseListQuery = useWarehouseList({ limit: 200 });
	const sortedWarehouses = useMemo(
		() =>
			[...(warehouseListQuery.data?.items ?? [])].sort((a, b) =>
				a.code.localeCompare(b.code, "tr"),
			),
		[warehouseListQuery.data?.items],
	);

	const filteredWarehouses = useMemo(() => {
		const term = warehouseFilter.trim().toLowerCase();
		if (!term) return sortedWarehouses;
		return sortedWarehouses.filter(
			(w) => w.code.toLowerCase().includes(term) || w.name.toLowerCase().includes(term),
		);
	}, [sortedWarehouses, warehouseFilter]);

	const selectedWarehouseId = warehouseId || sortedWarehouses[0]?.id || "";
	const selectedWarehouse = sortedWarehouses.find((w) => w.id === selectedWarehouseId);

	const categoriesQuery = useStockItemCategories();
	const categoryOptions = (categoriesQuery.data?.categories ?? []).map((c) => ({
		value: c.category,
		label: c.code != null ? `${c.category}(${c.code})` : c.category,
	}));

	const isAllScopeActive = scope === "all" && q.trim().length > 0;

	const warehouseStocksQuery = useWarehouseStocks(selectedWarehouseId, {
		search: q || undefined,
		category: category || undefined,
		page,
		limit: size,
	});
	const allStocksQuery = useWarehouseStockSearch({
		search: q || undefined,
		category: category || undefined,
		page,
		limit: size,
	});

	const activeQuery = isAllScopeActive ? allStocksQuery : warehouseStocksQuery;
	const result = activeQuery.data;

	function updateSearch(next: Partial<WarehousesSearch>) {
		navigate({
			to: ".",
			search: { warehouseId: selectedWarehouseId, scope, q, category, page, size, ...next },
		});
	}

	function handleSelectWarehouse(id: string) {
		updateSearch({ warehouseId: id, page: 1, category: "" });
	}

	const columns: {
		key: string;
		header: string;
		render?: (row: WarehouseStockRow) => React.ReactNode;
	}[] = [
		...(isAllScopeActive
			? [
					{
						key: "warehouseCode",
						header: t("warehouse"),
						render: (row: WarehouseStockRow) => <Badge variant="muted">{row.warehouseCode}</Badge>,
					},
				]
			: []),
		{
			key: "code",
			header: t("productCode"),
			render: (row: WarehouseStockRow) => (
				<Link to={`/stock-items/${row.stockItemId}` as never} className={styles.link}>
					{row.code}
				</Link>
			),
		},
		{ key: "name", header: t("productName") },
		{
			key: "currentStock",
			header: t("currentStock"),
			render: (row: WarehouseStockRow) => formatQuantity(row.currentStock),
		},
		{
			key: "reservedAmount",
			header: t("openOrderAmount"),
			render: (row: WarehouseStockRow) => formatQuantity(row.reservedAmount),
		},
		{
			key: "onOrderAmount",
			header: t("expectedAmount"),
			render: (row: WarehouseStockRow) => formatQuantity(row.onOrderAmount),
		},
	];

	const showEmptyAllScopePrompt = scope === "all" && q.trim().length === 0;

	return (
		<div>
			<PageHeader title={t("warehouses")} />

			<div className={styles.layout}>
				<div className={styles.sidebar}>
					<Card>
						<Input
							placeholder={t("warehouseSearchPlaceholder")}
							value={warehouseFilter}
							onChange={(e) => setWarehouseFilter(e.target.value)}
							className={styles.sidebarSearch}
						/>
						{warehouseListQuery.isLoading && <LoadingSpinner />}
						<div className={styles.warehouseList}>
							{filteredWarehouses.map((w) => (
								<button
									key={w.id}
									type="button"
									className={[
										styles.warehouseItem,
										w.id === selectedWarehouseId && styles.warehouseItemActive,
									]
										.filter(Boolean)
										.join(" ")}
									onClick={() => handleSelectWarehouse(w.id)}
								>
									<span className={styles.warehouseCode}>{w.code}</span>
									<span className={styles.warehouseName}>{w.name}</span>
								</button>
							))}
						</div>
					</Card>
				</div>

				<div className={styles.main}>
					<Card>
						{!selectedWarehouseId && <EmptyState message={t("selectWarehousePrompt")} />}

						{selectedWarehouseId && (
							<>
								<div className={styles.mainHeader}>
									<span className={styles.selectedLabel}>
										{t("selectedWarehouse")}: {selectedWarehouse?.code}
									</span>
									<div className={styles.badges}>
										<Badge variant="default">
											{t("inStockCount")}: {result?.total ?? 0}
										</Badge>
										<Badge variant="success">
											{t("totalProductStock")}: {formatQuantity(result?.totalStock)}
										</Badge>
									</div>
								</div>

								<div className={styles.filters}>
									<div className={styles.scopeToggle}>
										<button
											type="button"
											className={[styles.scopeButton, scope !== "all" && styles.scopeButtonActive]
												.filter(Boolean)
												.join(" ")}
											onClick={() => updateSearch({ scope: "visible", page: 1 })}
										>
											{t("visibleScope")}
										</button>
										<button
											type="button"
											className={[styles.scopeButton, scope === "all" && styles.scopeButtonActive]
												.filter(Boolean)
												.join(" ")}
											onClick={() => updateSearch({ scope: "all", page: 1 })}
										>
											{t("allScope")}
										</button>
									</div>
									<Input
										placeholder={t("codeNameSearchPlaceholder")}
										value={q}
										onChange={(e) => updateSearch({ q: e.target.value, page: 1 })}
										className={styles.search}
									/>
									<Select
										value={category}
										onChange={(e) => updateSearch({ category: e.target.value, page: 1 })}
										options={[{ value: "", label: t("productGroup") }, ...categoryOptions]}
										className={styles.categorySelect}
									/>
								</div>

								{showEmptyAllScopePrompt && (
									<EmptyState message={t("enterSearchForAllWarehouses")} />
								)}

								{!showEmptyAllScopePrompt && (
									<>
										{activeQuery.isError && <ErrorFallback onRetry={activeQuery.refetch} />}
										{!activeQuery.isError && (
											<div className={styles.tableScroll}>
												<Table
													columns={columns}
													data={result?.items ?? []}
													isLoading={activeQuery.isLoading}
													emptyMessage={t("noStockItemsInWarehouse")}
													keyExtractor={(row) => row.id}
												/>
											</div>
										)}
										<Pagination
											page={page}
											pageSize={size}
											total={result?.total ?? 0}
											onPageChange={(p) => updateSearch({ page: p })}
											onPageSizeChange={(s) => updateSearch({ size: s, page: 1 })}
										/>
									</>
								)}
							</>
						)}
					</Card>
				</div>
			</div>
		</div>
	);
}
